import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import FormControlLabel from '@mui/material/FormControlLabel'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import { concat, filter, find, keys, propEq, where } from 'ramda'
import React from 'react'

import Dialog from '@/app/component/atom/dialog'
import DialogHeader from '@/app/component/atom/dialog-header'
import SearchableSelect from '@/app/component/atom/form/searchable-select'
import ListSelect from '@/app/component/atom/list-select'
import { CountryTimezone, PersonalizationType, SingleTimezoneType, TimezoneWithCountryCodeType } from '@/app/types'

type Props = {
  campaignName: string
  countriesTimezones: CountryTimezone[]
  isOpen: boolean
  personalizationList: PersonalizationType
  timezone: string
  timezones: TimezoneWithCountryCodeType

  onClose: () => void
  onConfirm: (payload: { timezone: string; fields: string[]; labels: boolean }) => void
}

const defaultFields = ['contact.firstName', 'contact.lastName', 'contact.fullPhoneNumber']

const DownloadReportDialog: React.FC<Props> = ({
  campaignName,
  countriesTimezones = [],
  isOpen,
  personalizationList = {},
  timezone: initialTimezone,
  timezones: initialTimezones = {},
  onClose,
  onConfirm,
}) => {
  const tempCountryCode = keys(
    filter(where({ tzs: find(propEq('timezone', initialTimezone)) }))(initialTimezones),
  )[0] as string

  const [country, setCountry] = React.useState(tempCountryCode)
  const [countryTouched, setCountryTouched] = React.useState(false)
  const [timezone, setTimezone] = React.useState(initialTimezone)
  const [timezoneTouched, setTimezoneTouched] = React.useState(false)
  const [timezones, setTimezones] = React.useState<SingleTimezoneType[]>([])
  const [selectedFields, setSelectedFields] = React.useState(defaultFields)
  const [useLabels, setUseLabels] = React.useState(true)

  React.useEffect(() => {
    const tzs = initialTimezones[country]?.tzs || []
    setTimezones(tzs)
    setTimezone((s) => {
      if (!tzs.length) {
        return ''
      }

      if (!find(propEq('timezone', s), tzs)) {
        return tzs[0].timezone
      }

      return s
    })
  }, [country, initialTimezones])

  React.useEffect(() => {
    if (!isOpen) {
      setCountryTouched(false)
      setTimezoneTouched(false)
    }
  }, [isOpen])

  const { contact = [], customfields = [] } = personalizationList
  const fields = React.useMemo(
    () => concat(contact, customfields).filter((field) => field.value !== 'contact.id'),
    [contact, customfields],
  )

  return (
    <Dialog id="download-campaign-dialog" open={!!isOpen} onClose={onClose}>
      <DialogHeader title={`Configure the report for ${campaignName}`} onClose={onClose} />
      <DialogContent>
        <div>
          <FormControlLabel
            control={<Checkbox onChange={() => setUseLabels(!useLabels)} checked={useLabels} />}
            label="Show labels as answers instead of responses"
          />
        </div>
        <div>
          <Typography variant="subtitle1" color="textSecondary">
            What time zone do you want the dates and times in the report to be in?
          </Typography>
          <div
            style={{
              marginTop: '5px',
            }}
          >
            <SearchableSelect
              formControlProps={{
                margin: 'none',
              }}
              labelId="report-country-timezone-label"
              id="report-country-timezone-select"
              value={country}
              error={!country && countryTouched}
              label="Select Country"
              keyPropFn={(option) => option.value}
              valuePropFn={(option) => option.label}
              options={countriesTimezones}
              onBlur={() => setCountryTouched(true)}
              onChange={(selectedId) => {
                setCountry(selectedId)
              }}
              showAll
            />
          </div>
          <div>
            <SearchableSelect
              formControlProps={{
                margin: 'none',
              }}
              labelId="report-timezone-label"
              id="report-timezone-select"
              value={timezone}
              error={!timezone && timezoneTouched}
              label="Select Timezone"
              keyPropFn={(option) => option.timezone}
              valuePropFn={(option) => `${option.city} (${option.offset})`}
              onBlur={() => setTimezoneTouched(true)}
              onChange={(selectedId) => {
                setTimezone(selectedId)
              }}
              options={timezones}
              showAll
            />
          </div>
          <div
            style={{
              marginTop: '15px',
            }}
          >
            <Typography variant="subtitle1" color="textSecondary">
              Which contact fields do you want to include in the report?
            </Typography>
            <Typography variant="subtitle2" color="textSecondary">
              Contact ID is always included.
            </Typography>
            <div
              style={{
                marginTop: '5px',
              }}
            >
              <Paper sx={{ padding: (theme) => theme.spacing(0, 2) }}>
                <ListSelect
                  name="contact-fields"
                  options={fields}
                  selectedOptions={selectedFields}
                  onChange={setSelectedFields}
                />
              </Paper>
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          id="downloadcampaigns-confirm"
          variant="contained"
          color="primary"
          disabled={!timezone}
          onClick={() => onConfirm({ timezone, fields: selectedFields, labels: useLabels })}
        >
          Download the campaign report
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DownloadReportDialog
